import React, { useState, useEffect, useRef } from 'react';
import { Heart, X, MapPin, DollarSign, Briefcase, Zap, Send, ExternalLink, Info, MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useJobs } from '../hooks/useJobs';
import { useJobInterests } from '../hooks/useJobInterests';
import { useMessages } from '../hooks/useMessages';
import { useLinkedInImport } from '../hooks/useLinkedInImport';
import { useProfile } from '../hooks/useProfile';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';
import { supabase } from '../lib/supabase';
import ProfileSummary from '../components/ProfileSummary';
import RelevanceIndicator from '../components/RelevanceIndicator';
import DismissalReasonPicker from '../components/DismissalReasonPicker';

export default function CandidateView({ onStats }) {
  const { user, profile } = useAuth();
  const { personalizedJobs, fetchPersonalizedJobs, dismissJob } = useJobs();
  const { interests, swipeInterested, swipePass, subscribeToUpdates } = useJobInterests();
  const { sendMessage: sendMsg } = useMessages();
  const { saveLinkedInData, loadDemoData, loading: profileLoading, updateRelevanceProfile } = useProfile();
  const linkedIn = useLinkedInImport();

  const [currentJobIndex, setCurrentJobIndex] = useState(0);
  const [viewingMatch, setViewingMatch] = useState(false);
  const [matchIndex, setMatchIndex] = useState(0);
  const [message, setMessage] = useState('');
  const [messageSent, setMessageSent] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [demoError, setDemoError] = useState('');
  const [inviteConnections, setInviteConnections] = useState([]);
  const [showProfileSummary, setShowProfileSummary] = useState(false);
  const [showDismissalPicker, setShowDismissalPicker] = useState(false);
  const [dismissingJobId, setDismissingJobId] = useState(null);
  const [showVouchRequestModal, setShowVouchRequestModal] = useState(false);
  const [vouchRequestNote, setVouchRequestNote] = useState('');
  const [pendingSwipeJob, setPendingSwipeJob] = useState(null);
  const [declineMessages, setDeclineMessages] = useState({});
  const [viewingDeclines, setViewingDeclines] = useState(false);
  const [dismissedDeclineIds, setDismissedDeclineIds] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('vouch-dismissed-declines') || '[]'); } catch { return []; }
  });
  const fileInputRef = useRef(null);

  // Compute derived state
  const swipedJobIds = new Set(interests.map(i => i.job_id));
  const unseenJobs = personalizedJobs.filter(j => !swipedJobIds.has(j.job_id));
  const matches = interests.filter(i => i.status === 'connected');
  const declinedInterests = interests.filter(i => i.status === 'rejected');
  const visibleDeclines = declinedInterests.filter(i => !dismissedDeclineIds.includes(i.id));

  const dismissDecline = (interestId) => {
    const updated = [...dismissedDeclineIds, interestId];
    setDismissedDeclineIds(updated);
    sessionStorage.setItem('vouch-dismissed-declines', JSON.stringify(updated));
  };

  // Push stats to header
  useEffect(() => {
    if (onStats && profile?.linkedin_connected) {
      onStats({ role: 'candidate', jobCount: personalizedJobs.length, matchCount: matches.length });
    }
  }, [onStats, profile?.linkedin_connected, personalizedJobs.length, matches.length]);

  // Fetch personalized jobs on mount and when profile changes
  useEffect(() => {
    if (profile?.linkedin_connected) {
      fetchPersonalizedJobs();
    }
  }, [profile?.linkedin_connected, fetchPersonalizedJobs]);

  // Refetch when user switches back to this PWA
  useVisibilityRefresh(fetchPersonalizedJobs);

  // Subscribe to real-time interest updates
  useEffect(() => {
    const unsubscribe = subscribeToUpdates(() => {
      fetchPersonalizedJobs();
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch decline messages for rejected interests
  useEffect(() => {
    if (!user || declinedInterests.length === 0) return;
    const jiIds = declinedInterests.map(i => i.id);
    supabase
      .from('messages')
      .select('*, sender:sender_id(display_name)')
      .in('job_interest_id', jiIds)
      .eq('recipient_id', user.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (data) {
          const map = {};
          for (const msg of data) {
            if (!map[msg.job_interest_id]) map[msg.job_interest_id] = msg;
          }
          setDeclineMessages(map);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, declinedInterests.length]);

  // Fetch connections for invite list (Grow Your Network)
  useEffect(() => {
    if (user && profile?.linkedin_connected) {
      supabase
        .from('connections')
        .select('first_name, last_name, company')
        .eq('user_id', user.id)
        .limit(4)
        .then(({ data }) => { if (data) setInviteConnections(data); });
    }
  }, [user, profile?.linkedin_connected]);

  const handleSwipe = () => {
    if (unseenJobs.length === 0) return;
    const job = unseenJobs[currentJobIndex];
    if (swipedJobIds.has(job.job_id)) return;
    setPendingSwipeJob(job);
    setVouchRequestNote('');
    setShowVouchRequestModal(true);
  };

  const handleSendVouchRequest = async () => {
    if (!pendingSwipeJob) return;
    await swipeInterested(pendingSwipeJob.job_id, pendingSwipeJob.voucher_id, vouchRequestNote);
    setShowVouchRequestModal(false);
    setPendingSwipeJob(null);
    setVouchRequestNote('');
    if (currentJobIndex >= unseenJobs.length - 1) {
      setCurrentJobIndex(0);
    }
  };

  const handlePass = async () => {
    if (unseenJobs.length === 0) return;
    const passedJobId = unseenJobs[currentJobIndex].job_id;
    await swipePass(passedJobId);
    setDismissingJobId(passedJobId);
    setShowDismissalPicker(true);
    if (currentJobIndex >= unseenJobs.length - 1) {
      setCurrentJobIndex(0);
    }
  };

  const handleDismissalSelect = async (reason) => {
    if (dismissingJobId) {
      await dismissJob(dismissingJobId, reason);
    }
    setShowDismissalPicker(false);
    setDismissingJobId(null);
  };

  const handleDismissalSkip = () => {
    setShowDismissalPicker(false);
    setDismissingJobId(null);
  };

  const isDemo = user?.email?.endsWith('@vouch-demo.com');

  const handleImportComplete = async () => {
    await saveLinkedInData({
      profile: linkedIn.importedProfile,
      positions: linkedIn.importedPositions,
      connections: linkedIn.importedConnections,
    });
    setShowImportModal(false);
    linkedIn.reset();
    if (!isDemo) {
      setShowProfileSummary(true);
    }
    fetchPersonalizedJobs();
  };

  // LinkedIn Import Modal
  if (showImportModal) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-vouch-navy to-vouch-teal p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-bold">Connect Your LinkedIn</h2>
              <button onClick={() => { setShowImportModal(false); linkedIn.reset(); }}
                className="text-white hover:text-cyan-200">
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="p-4">
            {linkedIn.importStep === 1 && (
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-sm text-gray-900 mb-2">How to export your LinkedIn data:</h3>
                  <ol className="text-xs text-gray-700 space-y-1.5 list-decimal list-inside">
                    <li>Go to LinkedIn Settings & Privacy</li>
                    <li>Click "Get a copy of your data"</li>
                    <li>Select the data you want (Connections, Profile)</li>
                    <li>Request the archive and download when ready</li>
                  </ol>
                </div>
                <button onClick={() => linkedIn.setImportStep(2)}
                  className="w-full py-3 bg-vouch-navy text-white rounded-lg font-semibold hover:bg-vouch-navy-light">
                  I Have My Data Export
                </button>
                <button
                  onClick={async () => { await loadDemoData(); setShowImportModal(false); linkedIn.reset(); fetchPersonalizedJobs(); }}
                  disabled={profileLoading}
                  className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {profileLoading ? 'Loading demo data...' : 'Load Demo Data Instead'}
                </button>
              </div>
            )}

            {linkedIn.importStep === 2 && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                  <div className="text-4xl mb-2">📁</div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">Upload LinkedIn Export</p>
                  <p className="text-xs text-gray-500 mb-4">Drop your .zip file here or click to browse</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".zip"
                    className="hidden"
                    onChange={(e) => e.target.files[0] && linkedIn.handleFileUpload(e.target.files[0])}
                  />
                  <button onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-vouch-navy text-white rounded-lg text-sm font-semibold">
                    {linkedIn.loading ? 'Processing...' : 'Choose File'}
                  </button>
                </div>
                {linkedIn.error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                    {linkedIn.error}
                  </div>
                )}
                <button
                  onClick={async () => { await loadDemoData(); setShowImportModal(false); linkedIn.reset(); fetchPersonalizedJobs(); }}
                  disabled={profileLoading}
                  className="w-full py-2 text-gray-500 text-sm hover:text-gray-700 disabled:opacity-50">
                  {profileLoading ? 'Loading...' : 'Or load demo data instead'}
                </button>
              </div>
            )}

            {linkedIn.importStep === 3 && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-800 text-sm mb-2">Data Ready to Import</h3>
                  {linkedIn.importedProfile && (
                    <p className="text-xs text-green-700">
                      Profile: {linkedIn.importedProfile.firstName} {linkedIn.importedProfile.lastName}
                    </p>
                  )}
                  {linkedIn.importedPositions.length > 0 && (
                    <p className="text-xs text-green-700">
                      {linkedIn.importedPositions.length} positions found
                    </p>
                  )}
                  {linkedIn.importedConnections.length > 0 && (
                    <p className="text-xs text-green-700">
                      {linkedIn.importedConnections.length} connections found
                    </p>
                  )}
                </div>
                <button onClick={handleImportComplete}
                  className="w-full py-3 bg-gradient-to-r from-vouch-navy to-vouch-teal text-white rounded-lg font-semibold">
                  {linkedIn.loading ? 'Saving...' : 'Import & Continue'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Profile Summary (after import, before job deck)
  if (showProfileSummary && profile?.linkedin_connected) {
    return (
      <div className="max-w-lg mx-auto">
        <ProfileSummary
          profile={profile}
          onConfirm={async (updates) => {
            await updateRelevanceProfile(updates);
            setShowProfileSummary(false);
            fetchPersonalizedJobs();
          }}
          onSkip={() => setShowProfileSummary(false)}
        />
      </div>
    );
  }

  // Not connected yet - show setup
  if (!profile?.linkedin_connected) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl p-6 shadow text-center">
          <div className="text-5xl mb-4">🎯</div>
          <h2 className="text-xl font-bold text-vouch-navy mb-2">Find Your Next Role</h2>
          <p className="text-gray-600 text-sm mb-6">
            Connect your LinkedIn to discover jobs where people in your network can vouch for you.
          </p>
          <div className="bg-gradient-to-r from-slate-50 to-cyan-50 rounded-lg p-4 mb-6 border border-cyan-100">
            <div className="text-2xl font-bold text-vouch-navy blur-sm select-none">47 jobs</div>
            <p className="text-xs text-vouch-teal mt-1 font-medium">
              Unlock personalized matches with people who can vouch for you
            </p>
          </div>
          <button onClick={() => setShowImportModal(true)}
            className="w-full py-3 bg-vouch-navy text-white rounded-lg font-semibold hover:bg-vouch-navy-light flex items-center justify-center gap-2">
            Connect Your Network
          </button>
          <button
            onClick={async () => {
              setDemoError('');
              const result = await loadDemoData();
              if (result?.error) {
                setDemoError(result.error);
              } else {
                fetchPersonalizedJobs();
              }
            }}
            disabled={profileLoading}
            className="w-full mt-2 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            {profileLoading ? 'Loading demo data...' : 'Load Demo Data Instead'}
          </button>
          {demoError && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm text-left">
              {demoError}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Match view
  if (viewingMatch && matches.length > 0) {
    const match = matches[matchIndex];
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-gradient-to-br from-vouch-navy to-vouch-teal rounded-xl p-6 shadow-xl">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-white bg-white bg-opacity-20 px-3 py-1 rounded-full">
              {matchIndex + 1} of {matches.length}
            </span>
            <button onClick={() => setViewingMatch(false)}
              className="text-white hover:text-cyan-200 bg-white bg-opacity-20 rounded-full p-1">
              <X size={20} />
            </button>
          </div>
          <div className="text-center">
            <div className="text-5xl mb-3">🎉</div>
            <h2 className="text-xl font-bold text-white mb-4">Great News!</h2>
            {match.outcome && (
              <div className={`rounded-lg p-3 mb-3 ${
                match.outcome === 'strong_fit' || match.outcome === 'good_fit'
                  ? 'bg-green-400 bg-opacity-30 border border-green-300'
                  : 'bg-orange-400 bg-opacity-30 border border-orange-300'
              }`}>
                <p className="text-white text-sm font-semibold">
                  {match.outcome === 'strong_fit' && '🌟 The hiring team sees a strong fit!'}
                  {match.outcome === 'good_fit' && '👍 The hiring team is interested!'}
                  {match.outcome === 'not_aligned' && '↔️ The team moved in a different direction.'}
                  {match.outcome === 'underqualified' && '📊 The team is looking for different experience.'}
                  {match.outcome === 'other' && '💬 The team has provided an update.'}
                </p>
              </div>
            )}
            <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-4">
              <p className="text-white text-sm font-semibold mb-1">
                {match.jobs?.company}
              </p>
              <p className="text-cyan-100 text-xs">{match.jobs?.title}</p>
            </div>
            <div className="text-left mb-4">
              <label className="text-white text-sm font-semibold block mb-2">Introduce yourself:</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Hi! I'm excited about this opportunity..."
                className="w-full px-3 py-2 rounded-lg text-gray-900 text-sm h-24 resize-none"
              />
            </div>
            {messageSent ? (
              <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                <p className="text-white text-sm font-semibold">✓ Message sent!</p>
              </div>
            ) : (
              <button
                onClick={async () => {
                  if (message.trim() && match.jobs?.hiring_manager_id) {
                    await sendMsg(match.jobs.hiring_manager_id, match.id, message);
                    setMessageSent(true);
                    setTimeout(() => {
                      setViewingMatch(false);
                      setMessage('');
                      setMessageSent(false);
                    }, 1500);
                  } else {
                    setViewingMatch(false);
                  }
                }}
                className="w-full bg-white text-vouch-navy py-3 rounded-lg font-bold hover:bg-slate-50 flex items-center justify-center gap-2">
                <Send size={18} /> {message.trim() ? 'Send Message' : 'Close'}
              </button>
            )}
            {matches.length > 1 && (
              <div className="flex gap-2 mt-3">
                <button onClick={() => setMatchIndex(Math.max(0, matchIndex - 1))}
                  disabled={matchIndex === 0}
                  className="flex-1 bg-white bg-opacity-20 text-white py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                  Previous
                </button>
                <button onClick={() => setMatchIndex(Math.min(matches.length - 1, matchIndex + 1))}
                  disabled={matchIndex === matches.length - 1}
                  className="flex-1 bg-white bg-opacity-20 text-white py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Decline updates view
  if (viewingDeclines) {
    return (
      <div className="max-w-lg mx-auto space-y-4">
        <button onClick={() => setViewingDeclines(false)}
          className="text-sm text-gray-500 hover:text-gray-700">
          ← Back to jobs
        </button>

        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="bg-gradient-to-r from-orange-400 to-amber-500 p-4">
            <h3 className="font-bold text-white">Vouch Updates</h3>
            <p className="text-orange-100 text-sm">
              {visibleDeclines.length} {visibleDeclines.length === 1 ? 'update' : 'updates'} from your network
            </p>
          </div>
          <div className="p-4 space-y-3">
            {visibleDeclines.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No updates to show.</p>
            ) : (
              visibleDeclines.map(interest => {
                const msg = declineMessages[interest.id];
                return (
                  <div key={interest.id} className="bg-orange-50 border border-orange-200 rounded-lg p-3 relative">
                    <button
                      onClick={() => dismissDecline(interest.id)}
                      className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                      aria-label="Dismiss update"
                    >
                      <X size={14} />
                    </button>
                    <p className="font-semibold text-sm text-gray-900 pr-6">{interest.jobs?.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{interest.jobs?.company}</p>
                    {msg && (
                      <p className="text-xs text-orange-700 mt-2">
                        {msg.sender?.display_name} was unable to vouch: <span className="italic">"{msg.content}"</span>
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main candidate view - job swipe deck
  return (
    <div className="max-w-lg mx-auto space-y-4">
      {/* Stats bar */}
      {profile?.linkedin_connected && (
        <div className="bg-vouch-navy rounded-xl px-4 py-2.5">
          <div className="flex items-center justify-between">
            <p className="text-xs text-cyan-100">
              <Zap size={12} className="inline mr-0.5" />
              <strong className="text-white">{personalizedJobs.length} jobs</strong> in your network
            </p>
            <div className="flex items-center gap-2">
              {visibleDeclines.length > 0 && Object.keys(declineMessages).length > 0 && (
                <button
                  onClick={() => setViewingDeclines(true)}
                  className="flex items-center gap-1 bg-white bg-opacity-15 px-2.5 py-1 rounded-full hover:bg-opacity-25 transition-all"
                >
                  <Info size={12} className="text-orange-300" />
                  <span className="text-xs font-medium text-orange-200">{visibleDeclines.length} Update{visibleDeclines.length !== 1 ? 's' : ''}</span>
                </button>
              )}
              {matches.length > 0 && (
                <button
                  onClick={() => setViewingMatch(true)}
                  className="flex items-center gap-1 bg-gradient-to-r from-vouch-teal to-emerald-400 px-2.5 py-1 rounded-full animate-pulse hover:animate-none"
                >
                  <span className="text-sm">🎉</span>
                  <span className="text-xs font-bold text-white">{matches.length} {matches.length === 1 ? 'Match' : 'Matches'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Job Card */}
      {unseenJobs.length > 0 && currentJobIndex < unseenJobs.length ? (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="bg-gradient-to-r from-vouch-navy to-vouch-teal p-4">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{unseenJobs[currentJobIndex].logo}</span>
              <div>
                <div className="text-white font-bold">{unseenJobs[currentJobIndex].company}</div>
                <div className="text-cyan-100 text-sm">{unseenJobs[currentJobIndex].department}</div>
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-bold text-gray-900 text-lg">{unseenJobs[currentJobIndex].title}</h3>
              <RelevanceIndicator score={unseenJobs[currentJobIndex].relevance_score} />
            </div>
            <div className="flex flex-wrap gap-3 mb-3">
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <MapPin size={14} /> {unseenJobs[currentJobIndex].location}
              </div>
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <DollarSign size={14} /> {unseenJobs[currentJobIndex].salary_range}
              </div>
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <Briefcase size={14} /> {unseenJobs[currentJobIndex].job_type}
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-2">{unseenJobs[currentJobIndex].description}</p>
            {unseenJobs[currentJobIndex].url && (
              <a
                href={unseenJobs[currentJobIndex].url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-vouch-teal hover:text-vouch-navy font-medium mb-4"
              >
                View Full Job Description <ExternalLink size={13} />
              </a>
            )}
            {!unseenJobs[currentJobIndex].url && <div className="mb-4" />}

            {/* Vouch connection info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2 text-green-800 mb-1">
                <img src="/brand/vouch/assets/vector/vouch-icon.svg" alt="" className="w-5 h-5" />
                <span className="font-semibold text-sm">
                  {unseenJobs[currentJobIndex].voucher_name} can vouch for you
                </span>
              </div>
              {unseenJobs[currentJobIndex].connection_context && (
                <p className="text-xs text-green-600 italic ml-7">
                  {unseenJobs[currentJobIndex].connection_context}
                </p>
              )}
              <p className="text-xs text-green-700 ml-7 mt-1">
                Your vouch will be shared confidentially
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <button onClick={handlePass}
                className="flex-1 bg-gray-100 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-gray-200">
                <X size={16} /> Pass
              </button>
              <button onClick={handleSwipe}
                className="flex-1 bg-gradient-to-r from-vouch-navy to-vouch-teal text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2">
                <Heart size={16} /> Interested
              </button>
            </div>
            <p className="text-center text-xs text-gray-500 mt-2">
              {currentJobIndex + 1} of {unseenJobs.length}
            </p>
          </div>
        </div>
      ) : personalizedJobs.length === 0 && interests.length === 0 ? (
        // No vouch chain established yet — show "Grow Your Network"
        <div className="bg-white rounded-xl p-6 shadow">
          <div className="text-center mb-4">
            <div className="text-4xl mb-3">🚀</div>
            <h3 className="font-bold text-gray-900 mb-2">Grow Your Network</h3>
            <p className="text-sm text-gray-600">
              Invite connections to VOUCH to unlock job opportunities they can vouch for you on.
            </p>
          </div>
          {inviteConnections.length > 0 && (
            <div className="space-y-2 mb-4">
              {inviteConnections.map((conn, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-vouch-teal bg-opacity-20 flex items-center justify-center text-vouch-navy text-xs font-bold">
                      {conn.first_name?.[0]}{conn.last_name?.[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{conn.first_name} {conn.last_name}</p>
                      {conn.company && <p className="text-xs text-gray-500">{conn.company}</p>}
                    </div>
                  </div>
                  <button className="text-xs bg-vouch-navy text-white px-3 py-1.5 rounded-full font-semibold hover:bg-vouch-navy-light">
                    Invite
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">
              Jobs appear when people in your network join as vouchers and connect with hiring managers.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl p-6 shadow text-center">
          <div className="text-4xl mb-3">✨</div>
          <h3 className="font-bold text-gray-900 mb-2">That's All For Now!</h3>
          <p className="text-sm text-gray-600">
            You've reviewed all available opportunities. Check back later for new matches.
          </p>
        </div>
      )}

      {/* Dismissal reason picker */}
      {showDismissalPicker && (
        <DismissalReasonPicker
          onSelect={handleDismissalSelect}
          onSkip={handleDismissalSkip}
        />
      )}

      {/* Vouch request message modal */}
      {showVouchRequestModal && pendingSwipeJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-vouch-navy to-vouch-teal p-4 text-white">
              <h3 className="font-bold flex items-center gap-2">
                <MessageCircle size={18} /> Request a Vouch
              </h3>
              <p className="text-cyan-100 text-sm mt-1">
                Ask {pendingSwipeJob.voucher_name?.split(' ')[0]} to vouch for you
              </p>
            </div>
            <div className="p-4 space-y-4">
              {/* Job summary */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-semibold text-sm text-gray-900">{pendingSwipeJob.title}</p>
                <p className="text-xs text-gray-500">{pendingSwipeJob.company} — {pendingSwipeJob.location}</p>
              </div>

              {/* Message to voucher */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Note to {pendingSwipeJob.voucher_name?.split(' ')[0]} (optional):
                </label>
                <textarea
                  value={vouchRequestNote}
                  onChange={(e) => setVouchRequestNote(e.target.value)}
                  placeholder={`Hey ${pendingSwipeJob.voucher_name?.split(' ')[0]}, I'm really interested in this role because...`}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm h-24 resize-none focus:border-vouch-teal focus:outline-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowVouchRequestModal(false); setPendingSwipeJob(null); }}
                  className="flex-1 bg-gray-100 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-gray-200"
                >
                  <X size={16} /> Cancel
                </button>
                <button
                  onClick={handleSendVouchRequest}
                  className="flex-1 bg-gradient-to-r from-vouch-navy to-vouch-teal text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                >
                  <Send size={16} /> Send Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interest summary */}
      {interests.filter(i => i.status === 'interested').length > 0 && (
        <div className="bg-white rounded-xl p-4 shadow">
          <h4 className="font-semibold text-sm text-gray-900 mb-2">Pending Interests</h4>
          <div className="space-y-2">
            {interests.filter(i => i.status === 'interested').map(interest => (
              <div key={interest.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                <div>
                  <p className="text-sm font-medium text-gray-900">{interest.jobs?.title}</p>
                  <p className="text-xs text-gray-500">{interest.jobs?.company}</p>
                </div>
                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium">
                  Waiting for vouch
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
